/**
 * One food: pick an amount, see every nutrient for that amount, fill the gaps
 * with AI, add it to the diary. USDA values are never changed; AI estimates
 * are stored separately (per user) and badged "AI". Wrong or missing data can
 * be fixed: the fix goes to the user's own copy at once and to the owner's
 * review queue for everyone else.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { scaleNutrients } from '@engine/nutrients';
import { effectiveNutrients, itemFromFood, missingKeys, type EffectiveNutrients } from '../lib/foodNutrients';
import { labelCheckForFood, type FoodLabelCheck } from '../lib/labelCheck';
import { errorMessage, log } from '../lib/logger';
import type { CompleteNutrientsResponse, Food, FoodWithFill, MealItem, NutrientMap, NutrientSourceMap } from '../lib/types';
import { useApp } from '../state/app';
import { LabelCheckLine, TrustBadge } from './Badges';
import { Icon } from './Icon';
import { formFromValues, LabelPhotoFlow, type CorrectionInfo, type LabelFormState } from './LabelEntry';
import { NutrientPanel } from './NutrientPanel';
import { Skeleton, Spinner } from './ui';

/** The fix form for a stored food, starting from what it shows now. */
export function correctionFor(food: Food, values: NutrientMap): { initial: LabelFormState; correction: CorrectionInfo } {
  const mine: boolean = food.owner_id !== null;
  const serving = food.portions[0] ?? null;
  const ingredients: string | null = food.notes?.match(/Ingredients:\s*(.*)$/s)?.[1]?.trim() ?? null;
  return {
    initial: formFromValues({ name: food.name, brand: food.brand, per100g: values, serving: serving ? { label: serving.label, grams: serving.grams } : null, ingredients }),
    correction: {
      ownFoodId: mine ? food.id : null,
      targetFoodId: mine ? null : food.id,
      barcode: food.barcode ?? null,
      original: {
        source: mine ? 'mine' : 'database', name: food.name, brand: food.brand,
        serving_label: serving?.label ?? null, serving_grams: serving?.grams ?? null, nutrients: food.nutrients,
      },
      otherPortions: mine ? food.portions.slice(1) : food.portions,
    },
  };
}

export interface AmountUnit { id: string; label: string; grams: number }

export function unitsFor(portions: ReadonlyArray<{ label: string; grams: number }>): AmountUnit[] {
  return [
    ...portions.map((p, i) => ({ id: `p${i}`, label: p.label, grams: p.grams })),
    { id: 'g', label: 'g', grams: 1 },
    { id: 'oz', label: 'oz (28.35 g)', grams: 28.3495 },
    { id: 'lb', label: 'lb (453.6 g)', grams: 453.592 },
  ];
}

export function FoodDetail({ foodId, onAdd, addLabel = 'Add to diary', onDeleted }: {
  foodId: number;
  onAdd?: (item: MealItem) => void;
  addLabel?: string;
  onDeleted?: () => void;
}): ReactElement {
  const { source, toast } = useApp();
  // A fix can move the view to the user's own (corrected) copy of the food.
  const [activeId, setActiveId] = useState<number>(foodId);
  const [reload, setReload] = useState<number>(0);
  const [data, setData] = useState<FoodWithFill | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState<string>('1');
  const [unitId, setUnitId] = useState<string>('g');
  const [aiBusy, setAiBusy] = useState<boolean>(false);
  const [aiResult, setAiResult] = useState<CompleteNutrientsResponse | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [macroKcal, setMacroKcal] = useState<boolean>(false);
  const [fixing, setFixing] = useState<boolean>(false);

  useEffect(() => { setActiveId(foodId); setFixing(false); }, [foodId]);

  useEffect(() => {
    let cancelled: boolean = false;
    setData(null); setError(null); setAiResult(null); setAiError(null); setConfirmDelete(false); setMacroKcal(false);
    source.getFood(activeId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d.food.portions.length > 0) { setUnitId('p0'); setQty('1'); } else { setUnitId('g'); setQty('100'); }
      })
      .catch((err: unknown) => { if (!cancelled) setError(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [activeId, reload, source]);

  const eff: EffectiveNutrients | null = useMemo(() => (data ? effectiveNutrients(data.food, data.fill) : null), [data]);
  const check: FoodLabelCheck | null = useMemo(() => (data && eff ? labelCheckForFood(data.food, eff.values) : null), [data, eff]);
  const off: boolean = check !== null && (check.report.verdict === 'LABEL_BELOW_MACROS' || check.report.verdict === 'LABEL_ABOVE_MACROS');
  // "Count calories from the macros": the analyzer's best estimate per serving, per 100 g.
  const values: NutrientMap = useMemo(() => {
    if (!eff) return {};
    if (!macroKcal || !off || !check) return eff.values;
    return { ...eff.values, energy_kcal: (check.report.macroBestEstimateKcal * 100) / check.servingGrams };
  }, [eff, macroKcal, off, check]);
  const units: AmountUnit[] = useMemo(() => unitsFor(data?.food.portions ?? []), [data]);
  const unit: AmountUnit = units.find((u) => u.id === unitId) ?? units[units.length - 3]!;
  const q: number = Math.max(0, Number(qty) || 0);
  const grams: number = q * unit.grams;
  const scaled = useMemo(() => scaleNutrients(values, grams), [values, grams]);

  if (error !== null) return <div className="note err">{error}</div>;
  if (!data || !eff) return <Skeleton height={420} />;
  const { food, fill } = data;
  const isMine: boolean = food.owner_id !== null;

  if (fixing) {
    const fix = correctionFor(food, eff.values);
    return (
      <div className="stack" style={{ gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>Fix {food.name}</h2>
          <p className="faint" style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600 }}>
            {food.barcode ? `Barcode ${food.barcode}. ` : ''}{isMine ? 'Your own food.' : 'From the shared food database.'}
          </p>
        </div>
        <LabelPhotoFlow barcode={food.barcode ?? null} initial={fix.initial} correction={fix.correction}
          onCancel={() => setFixing(false)}
          onSaved={(saved) => { setFixing(false); if (saved.id === activeId) setReload((r) => r + 1); else setActiveId(saved.id); }} />
      </div>
    );
  }
  // Measured-only values: what the AI must not change.
  const measured = food.nutrients;
  const missing: string[] = missingKeys(eff.values);

  const fillWithAi = async (): Promise<void> => {
    setAiBusy(true); setAiError(null); setAiResult(null);
    try {
      const res: CompleteNutrientsResponse = await source.completeNutrients({
        food: { name: food.name, ...(food.category ? { category: food.category } : {}), ...(food.brand ? { brand: food.brand } : {}), ...(food.notes ? { details: food.notes } : {}) },
        known: measured,
        want: missingKeys(measured),
      });
      setAiResult(res);
      if (!res.isFood || Object.keys(res.values).length === 0) return;
      if (isMine) {
        const sources: NutrientSourceMap = { ...food.nutrient_sources };
        for (const k of Object.keys(res.values)) sources[k] = 'ai';
        const updated = await source.updateFoodNutrients(food.id, { ...food.nutrients, ...res.values }, sources, res.usage?.model ?? null);
        setData({ food: updated, fill: null });
      } else {
        const notes: string[] = [...res.assumptions, ...res.warnings];
        await source.saveAiFill(food.id, res.values, res.usage?.model ?? null, res.overallConfidence, notes);
        setData({ food, fill: { food_id: food.id, nutrients: res.values, model: res.usage?.model ?? null, confidence: res.overallConfidence, notes } });
      }
      toast(`AI filled ${Object.keys(res.values).length} nutrients`);
      log.info('food.ai_filled', { foodId: food.id, filled: Object.keys(res.values).length, cached: res.cached });
    } catch (err) {
      setAiError(errorMessage(err));
    } finally {
      setAiBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    try { await source.deleteFood(food.id); toast('Food deleted'); onDeleted?.(); } catch (err) { toast(errorMessage(err), 'err'); }
  };

  const add = (): void => {
    if (!onAdd || grams <= 0) return;
    const label: string = unit.id === 'g' ? 'g' : unit.id === 'oz' ? 'oz' : unit.id === 'lb' ? 'lb' : unit.label;
    const item: MealItem = itemFromFood(food, values, grams, q, label);
    if (macroKcal && off) item.preparation = 'calories from the macros (True Label)';
    onAdd(item);
  };

  const s = (k: string): string => (scaled[k] === undefined ? '—' : String(Math.round(scaled[k]! * (k === 'energy_kcal' ? 1 : 10)) / (k === 'energy_kcal' ? 1 : 10)));

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>{food.name}</h2>
        <div className="row" style={{ marginTop: 6, gap: 8, fontSize: 12.5, fontWeight: 600 }}>
          <TrustBadge source={food.source} count={eff.known} aiCount={Object.values(eff.sources).filter((x) => x === 'ai').length} />
          {food.brand && <span className="faint">{food.brand}</span>}
          {food.category && <span className="faint">{food.category}</span>}
          {food.fdc_id !== null && <span className="faint">FDC #{food.fdc_id}</span>}
          <button type="button" className="link-btn" style={{ marginLeft: 'auto', fontSize: 12.5 }} onClick={() => setFixing(true)}>
            <Icon name="flag" size={14} /> Wrong or missing info? Fix it
          </button>
        </div>
      </div>

      <div className="amount-row">
        <input className="input" type="number" min={0} step="any" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} aria-label="Amount" />
        <select className="input" value={unit.id} onChange={(e) => {
          const next = units.find((u) => u.id === e.target.value);
          if (next && (next.id === 'g') !== (unit.id === 'g')) setQty(next.id === 'g' ? String(Math.round(grams) || 100) : '1');
          setUnitId(e.target.value);
        }} aria-label="Unit">
          {units.map((u) => <option key={u.id} value={u.id}>{u.id.startsWith('p') ? `${u.label} (${u.grams} g)` : u.label}</option>)}
        </select>
      </div>

      {check && <LabelCheckLine report={check.report} servingLabel={check.servingLabel} />}
      {off && check && (
        <label className="check" style={{ fontSize: 13.5 }}>
          <input type="checkbox" checked={macroKcal} onChange={(e) => setMacroKcal(e.target.checked)} />
          Count calories from the macros ({Math.round(check.report.macroBestEstimateKcal)} kcal per serving) instead of the label ({check.report.declared.caloriesKcal})
        </label>
      )}

      <div className="macro-strip">
        <div><span>Calories</span><b className="num">{s('energy_kcal')}</b><small className="faint">kcal</small></div>
        <div><span style={{ color: 'var(--protein)' }}>Protein</span><b className="num">{s('protein_g')}</b><small className="faint">g</small></div>
        <div><span style={{ color: 'var(--carbs)' }}>Carbs</span><b className="num">{s('carbs_g')}</b><small className="faint">g</small></div>
        <div><span style={{ color: 'var(--fat)' }}>Fat</span><b className="num">{s('fat_g')}</b><small className="faint">g</small></div>
      </div>

      {onAdd && (
        <button type="button" className="btn btn-primary" onClick={add} disabled={grams <= 0} style={{ padding: 12 }}>
          <Icon name="plus" size={18} /> {addLabel} <span style={{ fontWeight: 600 }}>· {Math.round(grams * 10) / 10} g</span>
        </button>
      )}

      {missing.length > 0 && (
        <div className="ai-callout">
          <p>{missing.length} of 94 nutrients aren’t in {isMine ? 'this food' : 'USDA’s data for this food'}. AI can estimate them{isMine ? '' : ' (just for you, measured values stay untouched)'}.</p>
          <button type="button" className="btn btn-ai btn-sm" onClick={() => void fillWithAi()} disabled={aiBusy}>{aiBusy ? <Spinner /> : <Icon name="sparkle" size={16} />} {aiBusy ? 'Estimating… (up to a minute)' : 'Fill missing with AI'}</button>
        </div>
      )}
      {aiError !== null && <div className="note err">{aiError}</div>}
      {aiResult && (aiResult.warnings.length > 0 || aiResult.assumptions.length > 0 || !aiResult.isFood) && (
        <div className="note info">
          <strong>About the AI estimate{aiResult.overallConfidence !== null ? ` (confidence ${Math.round(aiResult.overallConfidence * 100)}%)` : ''}</strong>
          <ul>
            {!aiResult.isFood && <li>The AI didn’t recognise this as a food.</li>}
            {aiResult.assumptions.map((a) => <li key={`a${a}`}>{a}</li>)}
            {aiResult.warnings.map((w) => <li key={`w${w}`}>{w}</li>)}
          </ul>
        </div>
      )}
      {fill && !aiResult && <p className="faint" style={{ fontSize: 12.5, margin: 0, fontWeight: 600 }}>Values marked AI were estimated{fill.model ? ` by ${fill.model}` : ''}{fill.confidence !== null ? ` (confidence ${Math.round(fill.confidence * 100)}%)` : ''}. Measured values always take priority.</p>}

      <div>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
          <strong style={{ fontSize: 14 }}>Nutrients in {Math.round(grams * 10) / 10} g</strong>
        </div>
        <NutrientPanel values={scaled} sources={eff.sources} />
      </div>

      {isMine && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {confirmDelete
            ? <><span className="muted" style={{ fontWeight: 600 }}>Delete this food for good?</span><button type="button" className="btn btn-sm" onClick={() => setConfirmDelete(false)}>Keep</button><button type="button" className="btn btn-sm btn-danger" onClick={() => void remove()}><Icon name="trash" size={15} /> Delete</button></>
            : <button type="button" className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(true)}><Icon name="trash" size={15} /> Delete food</button>}
        </div>
      )}
    </div>
  );
}
